import { useEffect, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { router } from '@inertiajs/react'
import { X, CheckCircle, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { SharedProps } from '@/types'

export default function Flash() {
  const { flash } = usePage<SharedProps>().props
  const [visible, setVisible] = useState(false)
  const [current, setCurrent] = useState<{ type: 'notice' | 'alert'; message: string } | null>(null)

  useEffect(() => {
    if (flash.notice) {
      setCurrent({ type: 'notice', message: flash.notice })
      setVisible(true)
    } else if (flash.alert) {
      setCurrent({ type: 'alert', message: flash.alert })
      setVisible(true)
    }
  }, [flash.notice, flash.alert])

  // Auto-dismiss after 4 seconds
  useEffect(() => {
    if (!visible) return
    const t = setTimeout(() => setVisible(false), 4000)
    return () => clearTimeout(t)
  }, [visible])

  // Also clear on navigation
  useEffect(() => {
    const cleanup = router.on('start', () => setVisible(false))
    return cleanup
  }, [])

  if (!visible || !current) return null

  const isNotice = current.type === 'notice'

  return (
    <div
      className={cn(
        'fixed bottom-5 right-5 z-[9999] flex items-start gap-3 max-w-sm w-full rounded-xl border px-4 py-3 shadow-lg transition-all',
        isNotice
          ? 'bg-white border-green-200 text-green-800'
          : 'bg-white border-red-200 text-red-700'
      )}
      role="alert"
    >
      {isNotice ? (
        <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
      ) : (
        <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
      )}
      <p className="text-sm flex-1 font-medium">{current.message}</p>
      <button
        onClick={() => setVisible(false)}
        className="text-gray-300 hover:text-gray-500 shrink-0"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}
